import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {ActiveCartService} from '@spartacus/cart/base/core';
import {Cart} from '@spartacus/cart/base/root';
import {SetPoNumber} from '@app/cart/store/actions/po-number.action';
import {CheckoutReviewOrderComponentTexts} from '@app/model/order/dcp-order-entry.model';
import {DcpCartService, PO_NUMBER_ACTIONS} from '@app/product/services/dcp-cart.service';

@UntilDestroy()
@Component({
  selector: 'dcp-po-number',
  templateUrl: './purchase-order-number.component.html',
  styleUrls: ['./purchase-order-number.component.scss'],
})
export class PurchaseOrderNumberComponent implements OnInit, OnDestroy {

  poNumber: string;
  lastPoNumber: string;
  @Input() componentTexts: CheckoutReviewOrderComponentTexts;
  @ViewChild('poInput', {static: false}) input: any;

  constructor(
    private store: Store,
    private cartService: DcpCartService,
    private activeCartService: ActiveCartService,
  ) {
  }

  ngOnInit(): void {
    this.loadLastPoNumber();
    this.subscribeToPoNumberSave();
  }

  ngOnDestroy(): void {
    this.sendPoNumber(this.poNumber, false);
  }

  onPoInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.poNumber = target.value;
  }

  /**
   * Validates and sends the poNumber passed into it in the backend and reacts on success or error.
   *
   * @param poNumber to validate and store in the backend.
   * @param shouldPlaceOrder boolean that decides if an order should be placed or not.
   */
  sendPoNumber(poNumber: string, shouldPlaceOrder: boolean) {
    if (!poNumber && !this.lastPoNumber || this.lastPoNumber === poNumber) {
      if (shouldPlaceOrder) {
        this.notifyToPlaceOrder();
      }
    } else {
      this.notifyAboutPendingPoSaveRequest();
      this.cartService.savePoNumber(poNumber).pipe(untilDestroyed(this)).subscribe((cart) => {
          this.notifyAboutSuccessfulPoSaveRequest();
          if (shouldPlaceOrder) {
            this.notifyToPlaceOrder();
          }
          this.store.dispatch(new SetPoNumber({poNumber: cart.purchaseOrderNumber}));
        },
        () => this.notifyAboutSaveFailure()
      );
      this.lastPoNumber = poNumber;
    }
  }

  private notifyToPlaceOrder() {
    this.cartService.emitPoNumberMessage(PO_NUMBER_ACTIONS.placeOrder);
  }

  private notifyAboutSuccessfulPoSaveRequest() {
    this.cartService.emitPoNumberMessage(PO_NUMBER_ACTIONS.poNumberCurrentSaveRequestFinishedSuccessfully);
  }

  private notifyAboutPendingPoSaveRequest() {
    this.cartService.emitPoNumberMessage(PO_NUMBER_ACTIONS.poNumberCurrentSaveRequestPending);
  }

  /**
   * Notifies subscribers about an error.
   */
  notifyAboutSaveFailure() {
    this.cartService.emitPoNumberMessage(PO_NUMBER_ACTIONS.poNumberCurrentSaveRequestFinishedWithErrors);
  }

  /**
   * Subscribes to cartService.poNumberSubmit to get informed when the number should be stored.
   */
  subscribeToPoNumberSave() {
    this.cartService.getPoNumberMessage$().pipe(untilDestroyed(this)).subscribe(message => {
      if (message === PO_NUMBER_ACTIONS.sendPoNumber) {
        this.sendPoNumber(this.poNumber, true);
      }
    });
  }

  loadLastPoNumber() {
    this.activeCartService.getActive().pipe(untilDestroyed(this)).subscribe((c: Cart) => {
      this.poNumber = c.purchaseOrderNumber;
      this.lastPoNumber = this.poNumber;
    }).unsubscribe();
  }
}
